import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FlowPagePayment } from "@/components/FlowPagePayment.tsx";
import { FlowPageAlerts } from "@/components/FlowPageAlerts.tsx";
import { useFlow } from "@/contexts/FlowContext.tsx";
import { FlowPageContract } from "@/components/FlowPageContract.tsx";
import { FlowPagePaymentMethod } from "@/components/FlowPagePaymentMethod.tsx";
import { FlowPageSummary } from "@/components/FlowPageSummary.tsx";
import { FlowPageResult } from "@/components/FlowPageResult.tsx";
import FlowPageCaptureCard from "@/app/capture-card/components/flow-page-capture-card";
import RefusedQuoteScreen from "@/app/quote-refusal/components/refused-quote-screen";
import { CONTRACT_STATUS_REFUSED } from "@/app/quote-refusal/constants";
import {ConstantResource, FlowStepInterface, PaymentPlanResource, PaymentTiming} from "@/interfaces";
import { useExtendedUser } from "@/contexts/UserContext.tsx";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs.tsx";
import { useContract } from "@/hooks/useContract";
import { shouldShowAlertsStep } from "@/utils/contract-alerts";
import { nextReachableStepId, resolveResumeStepId } from "@/utils/flow-steps";
import { CircleCheck } from "lucide-react";
import clsx from "clsx";
import { clientDisplayName } from "@/utils/client-name";

export default function FlowPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loaded } = useExtendedUser();

  const { currentStep, setCurrentStep, contractId } = useFlow();

  const { data: contract, isLoading: contractLoading, error: contractError } =
    useContract(contractId ?? undefined);

  const [selectedPaymentPlan, setSelectedPaymentPlan] =
  useState<PaymentPlanResource | null>();
  // Only meaningful for a promotion that enforces its deadline; "immediately" is
  // the option that always earns the discount, so it is the safe default.
  const [paymentTiming, setPaymentTiming] = useState<PaymentTiming>("immediately");
  // Settling the whole balance today instead of on the plan's schedule. Only ever
  // offered on a plan that allows it, and never the default: the schedule is what
  // the customer was shown.
  const [payInFull, setPayInFull] = useState(false);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] =
    useState<ConstantResource | null>({
      id: "500",
      type: "constants",
      attributes: {
        group: "PAYMENT_METHOD_TYPES",
        key: "CREDIT_CARD",
        value: "Credit Card",
      },
    });
  
  useEffect(() => {
    if ((loaded && !user) || !contractId) {
      navigate("/login");
    }
  }, [loaded, navigate, user]);

  // Automatically select active subscription payment plan if exists
  useEffect(() => {
    const snapshotPlan = contract?.meta?.payment_snapshot?.paymentPlan ?? null;

    // The snapshot is a flat object while the rest of the flow reads JSON:API
    // resources, so wrap it before it reaches the components that expect
    // `attributes`.
    if (!selectedPaymentPlan && snapshotPlan) {
      setSelectedPaymentPlan({
        id: String(snapshotPlan.id),
        type: "payment-plans",
        attributes: {
          name: snapshotPlan.name,
          installments: snapshotPlan.installments,
          notes: snapshotPlan.notes,
          installment_dates:
            snapshotPlan.installmentDates ??
            (snapshotPlan.startDate ? [snapshotPlan.startDate] : []),
          every_month: snapshotPlan.everyMonth,
        },
      } as PaymentPlanResource);
    }

  }, [contract, selectedPaymentPlan, currentStep]);

  const CONTRACT_STATUS_ACTIVE = 1;
  const hasActiveSubscription = Boolean(contract?.relationships?.subscriptionActive);

  // A plan paid in full in one payment completes its subscription the moment the
  // money lands, so `subscriptionActive` goes null exactly when the contract
  // becomes most definitely paid. Falling back to the balance keeps a settled
  // contract looking settled instead of inviting a second payment.
  const contractTotal = Number(contract?.meta?.prices?.total ?? 0);
  const contractBalance = Number(contract?.meta?.prices?.balance ?? 0);
  const isPaidInFull = contractTotal - contractBalance > 0 && contractBalance <= 0;
  const hasSettledPayment = hasActiveSubscription || isPaidInFull;

  // The payment step settles the contract itself: it refetches as soon as the
  // charge goes through, which flips `hasSettledPayment` while the customer is
  // still looking at the success dialog. Dropping the step at that moment would
  // unmount the dialog along with the button that takes them to the confirmation,
  // and drop them on the alerts tab instead. Pinned while it is the active step,
  // released as soon as they move on.
  const [isPaymentStepPinned, setIsPaymentStepPinned] = useState(false);
  const [isReviewingRefusedQuote, setIsReviewingRefusedQuote] = useState(false);
  const hasSignature = Boolean(contract?.attributes?.signature_url);
  const isContractActive = Number(contract?.attributes?.status_id) === CONTRACT_STATUS_ACTIVE;
  const isContractAccepted = Boolean(
    contract?.attributes?.is_terms_accepted && contract?.attributes?.is_clauses_accepted
  );
  // A contract is unlocked for the next steps when the customer signed it in the
  // portal, when it was accepted/confirmed in the back office (manual activation),
  // or when its acceptance flags are set.
  const isContractSigned = hasSignature || isContractActive || isContractAccepted;
  // On-demand-only contracts: no committed amount due now, we only capture a
  // card on file (no charge, no subscription) instead of the installment flow.
  const isCardCaptureOnly = Boolean(contract?.meta?.card_capture_only);
  // Alerts off — company-wide, or refused for this contract — drops the whole
  // step: nothing registered on it would ever be acted on, so the tab is hidden
  // rather than shown with a "you will not receive any alerts" notice.
  const hasAlertsStep = shouldShowAlertsStep(contract);
  // The snapshot is copied into `selectedPaymentPlan` by an effect, so for one render after
  // a reload the state is still null while the contract plainly has a plan. Reading both
  // keeps every gate that depends on a plan steady from the first paint, instead of showing
  // a step as locked and unlocking it a tick later.
  const hasPaymentPlan = Boolean(
    selectedPaymentPlan ?? contract?.meta?.payment_snapshot?.paymentPlan
  );

  const isOffPlatformPayment = String(contract?.meta?.payment_snapshot?.paymentPlan?.selectedPaymentMethodTypeId || selectedPaymentMethodType?.id) !== '500';

  // The summary step is what fills these in, so every later step stays locked
  // until the client has a name and an email. The name can arrive as two halves
  // or as one `fullname` (see utils/client-name.ts), so the gate asks whether
  // there IS a name rather than for both halves — a client migrated from the
  // legacy `clients` columns has no halves and would have been locked out.
  const isClientDataEmpty = !clientDisplayName(user?.attributes) || !user?.attributes.client_email;

  const steps = [
    {
      id: "summary",
      title: t("fe.pages.flow.steps.summary.tab"),
      element: FlowPageSummary,
      isVisible: true,
      showIcon: true,
    },
    {
      id: "contract",
      title: t("fe.pages.flow.steps.contract.tab"),
      element: FlowPageContract,
      isDisabled: isClientDataEmpty,
      // Always visible: the customer must be able to go back and re-read the
      // contract after signing. The signature step itself locks once signed.
      isVisible: true,
      showIcon: true,
    },
    {
      id: "capture-card",
      title: t("fe.pages.flow.steps.captureCard.tab"),
      element: FlowPageCaptureCard,
      isDisabled: !isContractSigned,
      isVisible: isCardCaptureOnly,
      showIcon: true,
    },
    {
      id: "payment-method",
      title: t("fe.pages.flow.steps.paymentMethod.tab"),
      element: FlowPagePaymentMethod,
      isDisabled: !isContractSigned,
      isVisible: !isCardCaptureOnly,
      showIcon: true,
    },
    {
      id: "payment",
      title: t("fe.pages.flow.steps.payment.tab"),
      element: FlowPagePayment,
      isDisabled: !isContractSigned || !hasPaymentPlan,
      isVisible:
        !isCardCaptureOnly &&
        !isOffPlatformPayment &&
        (!hasSettledPayment || isPaymentStepPinned),
      showIcon: true,
    },
    {
      id: "alerts",
      title: t("fe.pages.flow.steps.alerts.tab"),
      element: FlowPageAlerts,
      isDisabled: isClientDataEmpty,
      isVisible: hasAlertsStep,
      showIcon: false,
    },
    {
      id: "result",
      title: "Confirmation",
      element: FlowPageResult,
      // A card-on-file contract commits to no amount, so it has no plan to wait for —
      // gating it on one would leave the step permanently unreachable.
      isDisabled: !isContractSigned || (!isCardCaptureOnly && !hasPaymentPlan),
      // Always visible: this is where every shape of contract ends, and for a
      // card-on-file one it is the only step after the card is saved.
      isVisible: true,
      showIcon: false,
    },
  ] as FlowStepInterface[];

  const visibleSteps = steps.filter(step => step.isVisible);

  // Navigation is keyed on the step *id*, never on an index into `visibleSteps`.
  // That list changes shape while the user is in it (e.g. the payment tab
  // appears/disappears with the selected plan), so a stored index silently
  // points at a different step after the transition.
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);

  const visibleStepIds = visibleSteps.map(step => step.id).join(",");

  // Index persisted in sessionStorage, read once: it is only a resume hint, and
  // must not feed back into the navigation state we write back to it.
  const [resumeIndex] = useState(currentStep);

  // Resolved synchronously so the flow never renders a frame with no active
  // step: on first load it falls back to the persisted index, and a step that
  // just disappeared resolves to the next visible one instead of blanking.
  const activeStepId = useMemo(() => {
    const storedId =
      currentStepId ?? resolveResumeStepId(visibleSteps.map(step => step.id), resumeIndex);

    let resolved = visibleSteps.some(step => step.id === storedId)
      ? storedId
      : steps.slice(steps.findIndex(step => step.id === storedId) + 1)
          .find(step => step.isVisible)?.id ?? visibleSteps[0]?.id;

    // The payment and confirmation steps are meaningless without a plan.
    const canFallBackToPaymentMethod =
      !isCardCaptureOnly && visibleSteps.some(step => step.id === "payment-method");
    if (
      canFallBackToPaymentMethod &&
      !hasPaymentPlan &&
      (resolved === "payment" || resolved === "result")
    ) {
      resolved = "payment-method";
    }

    return resolved ?? null;
  }, [currentStepId, visibleStepIds, hasPaymentPlan, isCardCaptureOnly]);

  const activeIndex = Math.max(
    0,
    visibleSteps.findIndex(step => step.id === activeStepId)
  );

  // Keep the persisted index in sync so a reload resumes on the same step.
  useEffect(() => {
    setCurrentStep(activeIndex);
  }, [activeIndex]);

  useEffect(() => {
    setIsPaymentStepPinned(activeStepId === "payment");
  }, [activeStepId]);

  // Each plan carries its own promotion, so the answer to "when would you like to
  // pay?" belongs to the plan it was given for. Leaving it latched would forfeit a
  // discount on a plan the customer never declined one on.
  //
  // Paying in full is the same: it is one plan's offer, and a plan that does not
  // make it would otherwise inherit a tick nobody gave it.
  useEffect(() => {
    setPaymentTiming("immediately");
    setPayInFull(false);
  }, [selectedPaymentPlan?.id, selectedPaymentMethodType?.id]);

  // Only skip steps that are *not visible*: a step can still be flagged
  // disabled here because the contract refetch that unlocks it is in flight.
  const nextStep = useCallback(() => {
    const index = steps.findIndex(step => step.id === activeStepId);
    const next = steps.slice(index + 1).find(step => step.isVisible);
    if (next) setCurrentStepId(next.id);
  }, [activeStepId, visibleStepIds]);

  if (!user) return <></>;
  if (contractLoading) return <></>;
  if (contractError || !contract) return <></>;

  if (Number(contract.attributes?.status_id) === CONTRACT_STATUS_REFUSED && !isReviewingRefusedQuote) {
    return <RefusedQuoteScreen onReview={() => setIsReviewingRefusedQuote(true)} />;
  }

  return (
    <div className="max-w-full pb-7">
      <Tabs value={activeIndex.toString()} className="w-full mb-8">
        <div className="md:p-3 p-2 follo-card">
          <div className="overflow-x-auto">
            <TabsList className="w-max min-w-full inline-flex">
              {visibleSteps.map((step, index) => {
                const disabled = step.isDisabled ?? false;
                const isActive = activeIndex === index;
                const isFuture = activeIndex < index;
                return (
                  <TabsTrigger
                    key={step.id}
                    className={clsx(
                      "text-sm font-medium whitespace-nowrap flex-shrink-0 min-w-[150px] transition-colors",
                      
                      isActive && "!bg-white shadow-sm",

                      // Future but enabled
                      !disabled && isFuture && "opacity-60",

                      // Disabled (visually distinct)
                      disabled && "opacity-30 cursor-not-allowed bg-muted text-muted-foreground",

                      // Enabled hover state
                      !disabled && "cursor-pointer hover:bg-muted/40"
                    )}
                    onClick={() => !disabled && setCurrentStepId(step.id)}
                    value={index.toString()}
                    disabled={disabled}
                  >
                    {step.showIcon && hasSettledPayment && (
                      <CircleCheck size={16} className="mr-1 text-green-600" />
                    )}
                    {step.title}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>
        </div>
        {/*{steps.map((step, index) => {*/}
        {/*  const Component = step.element;*/}
        {/*  return <TabsContent value={index.toString()}>*/}
        {/*    <Component*/}
        {/*      key={index}*/}
        {/*      currentlyVisible={currentStep === index}*/}
        {/*      nextStep={nextStep}*/}
        {/*    />*/}
        {/*  </TabsContent>*/}
        {/*})}*/}
      </Tabs>

      {/*<FlowSteps*/}
      {/*  currentStep={currentStep}*/}
      {/*  setCurrentStep={setCurrentStep}*/}
      {/*  steps={steps}*/}
      {/*/>*/}

      {visibleSteps.map((step) => {
        const Component = step.element;

        return (
          <Component
            key={step.id}
            currentlyVisible={activeStepId === step.id}
            hasNextStep={Boolean(nextReachableStepId(steps, step.id))}
            nextStep={nextStep}
            selectedPaymentPlan={selectedPaymentPlan}
            setSelectedPaymentPlan={setSelectedPaymentPlan}
            selectedPaymentMethodType={selectedPaymentMethodType}
            setSelectedPaymentMethodType={setSelectedPaymentMethodType}
            paymentTiming={paymentTiming}
            setPaymentTiming={setPaymentTiming}
            payInFull={payInFull}
            setPayInFull={setPayInFull}
          />
        );
      })}
    </div>
  );
}
